import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ServiceStatusMessage, ServiceStatusState } from '@/entities/realtime-agent/types/service-status-messages';

interface ServiceStatusStore extends ServiceStatusState {
  // Actions
  setServiceStatus: (status: ServiceStatusMessage | null) => void;
  setVisible: (visible: boolean) => void;
  setShouldRetry: (shouldRetry: boolean) => void;
  setShouldShowUI: (shouldShow: boolean) => void;
  reset: () => void;
}

export const useServiceStatusStore = create<ServiceStatusStore>()(
  devtools(
    (set) => ({
      // Initial state
      currentStatus: null,
      isVisible: false,
      shouldRetry: true,
      shouldShowUI: false,

      // Actions
      setServiceStatus: (currentStatus) => {
        set({ currentStatus }, false, 'setServiceStatus');

        // 상태에 따라 재시도 여부와 UI 표시 여부 자동 결정
        if (currentStatus) {
          const { shouldRetry, shouldShowUI } = determineStatusBehavior(currentStatus);
          set({
            shouldRetry,
            shouldShowUI,
            isVisible: shouldShowUI
          }, false, 'setServiceStatusBehavior');
        } else {
          set({
            shouldRetry: true,
            shouldShowUI: false,
            isVisible: false
          }, false, 'clearServiceStatus');
        }
      },

      setVisible: (isVisible) =>
        set({ isVisible }, false, 'setVisible'),

      setShouldRetry: (shouldRetry) =>
        set({ shouldRetry }, false, 'setShouldRetry'),

      setShouldShowUI: (shouldShowUI) =>
        set({ shouldShowUI }, false, 'setShouldShowUI'),

      reset: () =>
        set({
          currentStatus: null,
          isVisible: false,
          shouldRetry: true,
          shouldShowUI: false,
        }, false, 'reset'),
    }),
    {
      name: 'service-status-store',
    }
  )
);

// 상태 메시지에 따른 동작 결정
function determineStatusBehavior(status: ServiceStatusMessage): {
  shouldRetry: boolean;
  shouldShowUI: boolean;
} {
  switch (status.type) {
    case 'server_maintenance':
      // 서버 점검중 - 재시도 완전 차단, UI 표시
      return { shouldRetry: false, shouldShowUI: true };

    case 'service_unavailable':
      // 서비스 준비중 - 재시도 완전 차단, UI 표시
      return { shouldRetry: false, shouldShowUI: true };

    case 'service_status':
      if (status.status === 'busy') {
        // 서비스 혼잡 - 재시도 완전 차단, UI 표시 (수동 재시도만 허용)
        return { shouldRetry: false, shouldShowUI: true };
      }
      return { shouldRetry: true, shouldShowUI: false };

    case 'admin_notice':
      // 관리자 공지 - 모든 우선순위에서 재시도 완전 차단
      return { shouldRetry: false, shouldShowUI: true };

    default:
      return { shouldRetry: true, shouldShowUI: false };
  }
}
